import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";

export const SEV_CLS = { bassa: "bg-slate-100 text-slate-700", media: "bg-amber-100 text-amber-800", alta: "bg-orange-100 text-orange-800", emergenza: "bg-red-600 text-white" };

export const useMapMeta = () => {
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get("/mappe/meta").then((r) => setMeta(r.data));
  }, []);
  return meta;
};

export const CriticitaFormDialog = ({ open, onOpenChange, preset, onCreated }) => {
  const meta = useMapMeta();
  const nav = useNavigate();
  const [f, setF] = useState({});
  const [cims, setCims] = useState([]);
  const [areas, setAreas] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setF({ cimitero_id: preset?.cimitero_id || "", area_id: preset?.area_id || "", category: "altro", severity: "media", title: "", description: "", provisional_measures: "" });
    setPhoto(null);
    api.get("/commesse").then((r) => setCims(r.data.flatMap((c) => c.cimiteri)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (f.cimitero_id) api.get(`/cimiteri/${f.cimitero_id}/map`).then((r) => setAreas(r.data.areas)).catch(() => setAreas([]));
  }, [f.cimitero_id]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/criticita", { ...f, area_id: f.area_id || null });
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await api.post(`/criticita/${data.id}/photos`, fd);
      }
      toast.success(`Criticità ${data.code} segnalata`);
      onOpenChange(false);
      onCreated ? onCreated(data) : nav(`/criticita/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!meta) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-xl overflow-y-auto rounded-2xl" data-testid="criticita-form-dialog">
        <DialogHeader><DialogTitle className="text-xl font-extrabold">Segnala un problema</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1"><Label>Cimitero *</Label><NativeSelect className="w-full" testid="cr-form-cimitero" value={f.cimitero_id} onChange={(v) => setF((x) => ({ ...x, cimitero_id: v, area_id: "" }))}>
            <option value="">Seleziona…</option>{cims.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect></div>
          <div className="space-y-1"><Label>Zona</Label><NativeSelect className="w-full" testid="cr-form-area" value={f.area_id} onChange={(v) => set("area_id", v)}>
            <option value="">— nessuna —</option>{areas.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}</NativeSelect></div>
          <div className="space-y-1"><Label>Categoria *</Label><NativeSelect className="w-full" testid="cr-form-category" value={f.category} onChange={(v) => set("category", v)}>
            {Object.entries(meta.categories).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</NativeSelect></div>
          <div className="space-y-1"><Label>Gravità *</Label><NativeSelect className="w-full" testid="cr-form-severity" value={f.severity} onChange={(v) => set("severity", v)}>
            {meta.severities.map((s) => <option key={s} value={s}>{s}</option>)}</NativeSelect></div>
          {f.severity === "emergenza" && <p className="rounded-xl bg-red-600 p-3 text-sm font-semibold text-white sm:col-span-2">Emergenza: verranno allertati subito CEO, responsabile e sicurezza. In caso di pericolo segui le procedure aziendali di emergenza, non affidarti solo all'app.</p>}
          <div className="space-y-1 sm:col-span-2"><Label>Titolo *</Label><Input value={f.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="cr-form-title" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Descrizione</Label><Textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="cr-form-description" /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Misure provvisorie adottate</Label><Input value={f.provisional_measures || ""} onChange={(e) => set("provisional_measures", e.target.value)} /></div>
          <div className="space-y-1 sm:col-span-2"><Label>Fotografia</Label><Input type="file" accept="image/*" capture="environment" onChange={(e) => setPhoto(e.target.files[0])} data-testid="cr-form-photo" /></div>
        </div>
        <DialogFooter><Button disabled={busy || !f.cimitero_id || (f.title || "").length < 3} onClick={submit} className="h-11 rounded-xl font-bold" data-testid="cr-form-submit">Invia segnalazione</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
